import { cloneDeep } from '@graphcommerce/graphql'
import type { FilterEqualTypeInput } from '@graphcommerce/graphql-mesh'
import { responsiveVal, extendableComponent, ChipMenu, ChipMenuProps } from '@graphcommerce/next-ui'
import {
  Box,
  Checkbox,
  FormControlLabel,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  listItemTextClasses,
} from '@mui/material'
import type { SetRequired } from 'type-fest'
import { useProductListLinkReplace, useProductListParamsContext } from '@graphcommerce/magento-product'
import { ProductListFiltersFragment } from './ProductListFilters.gql'
import { ProductListLink } from './ProductListLink'
import IconBxChevronUp from '../icons/IconBxChevronUp'
import { useRef, useState } from 'react'

type OwnerState = {
  isColor: boolean
  isActive: boolean
}
const componentName = 'FilterEqual' as const
const parts = [
  'listItem',
  'listItemInnerContainer',
  'checkbox',
  'linkContainer',
  'button',
  'resetButton',
  'filterAmount',
  'filterLabel',
  'isColor',
  'isActive',
] as const

const { classes, selectors, withState } = extendableComponent<
  OwnerState,
  typeof componentName,
  typeof parts
>(componentName, parts)

export type FilterIn = SetRequired<Omit<FilterEqualTypeInput, 'eq'>, 'in'>

type Filter = NonNullable<NonNullable<ProductListFiltersFragment['aggregations']>[number]>

type FilterEqualTypeProps = Filter & Omit<ChipMenuProps, 'selected'>

export function FilterEqualType(props: FilterEqualTypeProps) {
  const { attribute_code, count, label, options, __typename, ...chipProps } = props
  const { params } = useProductListParamsContext()
  const replaceRoute = useProductListLinkReplace({ scroll: false })

  const currentFilter: FilterEqualTypeInput = cloneDeep(params.filters[attribute_code]) ?? {
    in: [],
  }

  const anyFilterActive = Object.keys(params?.filters ?? {}).length >= 1

  const currentLabels =
    options
      ?.filter((option) => option && currentFilter.in?.includes(option.value))
      .map((option) => option && option.label) ?? []

  const removeFilter = () => {
    const linkParams = cloneDeep(params)
    delete linkParams.filters[attribute_code]
    delete linkParams.currentPage

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    replaceRoute(linkParams)
  }

  const [active, setActive] = useState(false)

  const wrapper = useRef<any>();
  const contentRef = useRef<any>()
  const height = `${contentRef?.current?.clientHeight}px`;

  return (

    // <ChipMenu
    //   variant='outlined'
    //   {...chipProps}
    //   label={label}
    //   selected={currentLabels.length > 0}
    //   selectedLabel={currentLabels.length > 0 ? currentLabels.join(', ') : undefined}
    //   onDelete={currentLabels.length > 0 ? removeFilter : undefined}
    //   className={componentName}
    // >

    < Box
      ref={wrapper}
      className={`${classes.linkContainer} mt-4`}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(1, minmax(0, 1fr))', md: 'repeat(1, 1fr)' },
        columnGap: responsiveVal(2, 3),
        minWidth: 0,
      }
      }
    >
      <div className='w-full text-sm flex justify-between items-center'
        style={{ borderTop: '1px solid #e5e5e5', padding: '10px 0px' }}
        onClick={() => setActive(!active)}
      >
        <span className='font-semibold md:font-normal'>
          {label}
        </span>
        <IconBxChevronUp width={22} height={22} className={`md:hidden ${!active && 'rotate-180'} text-light-400`} />
      </div>
      <div className={`max-h-[315px] overflow-auto small-scrollbar md:!h-auto md:!opacity-100`}

        style={{
          height: active === true ? height : '0px',
          opacity: active === true ? 1 : 0,
          transitionProperty: 'all',
          transitionDuration: '.2s',
          transitionTimingFunction: "linear"
        }}
      >

        <div ref={contentRef}>
          {
            options?.map((option) => {
              if (!option?.value) return null
              const labelId = `filter-equal-${attribute_code}-${option?.value}`
              const filters = cloneDeep(params.filters)
              const isColor = !!attribute_code?.toLowerCase().includes('color')
              const isActive = Boolean(currentFilter.in?.includes(option?.value))

              const cls = withState({ isColor, isActive })
              if (currentFilter.in?.includes(option.value)) {
                filters[attribute_code] = {
                  ...currentFilter,
                  in: currentFilter.in?.filter((v) => v !== option.value),
                }
              } else {
                filters[attribute_code] = {
                  ...currentFilter,
                  in: [...(currentFilter?.in ?? []), option.value],
                }
              }

              return (
                <ProductListLink
                  {...params}
                  filters={filters}
                  currentPage={undefined}
                  key={option?.value}
                  color='inherit'
                  link={{ replace: anyFilterActive, prefetch: false }}
                  className='flex justify-between items-center !no-underline'
                >
                  <FormControlLabel control={<Checkbox checked={isActive} />} label={option.label} classes={{ label: "!text-sm", root: "!mr-0" }} />

                  <span>({option.count})</span>
                </ProductListLink>
              )
            })
          }

        </div>
      </div>
    </Box >
    // </ChipMenu>

  )
}

FilterEqualType.selectors = selectors
